import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

/**
 * Handles OAuth deep link callbacks in the native apps.
 * Supports multiple callback URL patterns:
 * - nomiqa://oauth-callback?access_token=...
 * - com.nomiqa.app://oauth-callback?access_token=...
 * - com.nomiqa.app://app/auth#access_token=...
 * 
 * We intentionally avoid logging the full URL because it can contain auth tokens.
 */
export const useDeepLinkAuth = () => {
  const navigate = useNavigate();
  const lastHandledUrlRef = useRef<string | null>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleDeepLink = async (event: URLOpenListenerEvent) => {
      const url = event.url || '';

      if (processingRef.current) return;

      // Accept both token-bearing callbacks (broker/implicit) AND code-bearing
      // callbacks (direct Supabase PKCE flow).
      const hasTokens = url.includes('access_token') || url.includes('refresh_token');
      const hasCode = /[?&#]code=/.test(url);
      if (!hasTokens && !hasCode) return;

      if (lastHandledUrlRef.current === url) return;
      lastHandledUrlRef.current = url;
      processingRef.current = true;

      console.log('[DeepLink] OAuth callback received');

      try {
        // ---------- PKCE code path (direct Supabase flow) ----------
        if (hasCode && !hasTokens) {
          // Extract `code` from query OR hash
          let codeStr: string | null = null;
          const qIdx = url.indexOf('?');
          const hIdx = url.indexOf('#');
          const tail = [qIdx, hIdx].filter((i) => i !== -1).sort((a, b) => a - b)[0];
          if (tail !== undefined) {
            // Combine query + hash so URLSearchParams can find code in either
            const combined = url.substring(tail + 1).replace('#', '&');
            codeStr = new URLSearchParams(combined).get('code');
          }

          if (!codeStr) {
            console.warn('[DeepLink] code= present but could not be parsed');
            processingRef.current = false;
            return;
          }

          console.log('[DeepLink] Exchanging PKCE code for session...');
          const { error } = await supabase.auth.exchangeCodeForSession(codeStr);

          try { await Browser.close(); } catch {}

          if (error) {
            console.warn('[DeepLink] exchangeCodeForSession failed:', error.message);
            navigate('/app/auth', { replace: true });
          } else {
            console.log('[DeepLink] Session established via PKCE');
            navigate('/app/auth', { replace: true });
          }
          processingRef.current = false;
          return;
        }

        // ---------- Implicit token path (broker fallback) ----------
        const parsedParams = new URLSearchParams();
        const queryIndex = url.indexOf('?');
        const hashIndex = url.indexOf('#');

        if (queryIndex !== -1) {
          const queryEnd = hashIndex !== -1 && hashIndex > queryIndex ? hashIndex : undefined;
          const query = url.substring(queryIndex + 1, queryEnd);
          new URLSearchParams(query).forEach((value, key) => parsedParams.set(key, value));
        }

        if (hashIndex !== -1) {
          const hash = url.substring(hashIndex + 1);
          new URLSearchParams(hash).forEach((value, key) => parsedParams.set(key, value));
        }

        if (!parsedParams.toString()) {
          console.warn('[DeepLink] No token fragment found in callback URL');
          processingRef.current = false;
          return;
        }

        const accessToken = parsedParams.get('access_token');
        const refreshToken = parsedParams.get('refresh_token');

        if (!accessToken || !refreshToken) {
          console.warn('[DeepLink] Missing required tokens');
          processingRef.current = false;
          return;
        }

        console.log('[DeepLink] Setting session from tokens...');
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        try { await Browser.close(); } catch {}

        if (error) {
          console.warn('[DeepLink] Failed to set session:', error.message);
          navigate('/app/auth', { replace: true });
        } else {
          console.log('[DeepLink] Session set successfully, navigating to app...');
          navigate('/app/auth', { replace: true });
        }
      } catch (err) {
        console.warn('[DeepLink] OAuth callback error');
        navigate('/app/auth', { replace: true });
      } finally {
        processingRef.current = false;
      }
    };

    // Listen for deep link events
    const listener = App.addListener('appUrlOpen', handleDeepLink);

    // Also check if the app was opened with a URL (cold start)
    App.getLaunchUrl().then((result) => {
      if (result?.url) {
        handleDeepLink({ url: result.url });
      }
    });

    return () => {
      listener.then((l) => l.remove());
      lastHandledUrlRef.current = null;
      processingRef.current = false;
    };
  }, [navigate]);
};
